import type { useContextMenu } from '../../hooks/useContextMenu.ts';
import type { useFolderActions } from '../../hooks/useFolderActions.ts';
import { ContextMenu } from './ContextMenu.tsx';
import { NewFolderDialog } from './NewFolderDialog.tsx';
import { PreviewModal } from './PreviewModal.tsx';
import { ShareFolderDialog } from './ShareFolderDialog.tsx';
import { buildActions } from './fileActions.tsx';
import { joinPath } from '../../lib/format.ts';
import type { ApiNode, Namespace } from '../../api/types.ts';

/** 목록 위에 겹쳐 뜨는 것들의 열림 상태. */
export interface OverlayState {
  menuTarget: ApiNode | null;
  previewNode: ApiNode | null;
  shareTarget: ApiNode | null;
  newFolderOpen: boolean;
}

/** 목록 쪽에서 오버레이를 여닫는 손잡이. */
export interface OverlayControls {
  setMenuTarget: (node: ApiNode | null) => void;
  setPreviewNode: (node: ApiNode | null) => void;
  setDrawerNode: (node: ApiNode | null) => void;
  setShareTarget: (node: ApiNode | null) => void;
  setRenamingPath: (path: string | null) => void;
  setNewFolderOpen: (open: boolean) => void;
}

interface Props {
  ns: Namespace;
  path: string;
  items: ApiNode[];
  selected: string[];
  canEdit: boolean;
  sharingEnabled: boolean;
  actions: ReturnType<typeof useFolderActions>;
  menu: ReturnType<typeof useContextMenu>;
  state: OverlayState;
  controls: OverlayControls;
}

/**
 * 컨텍스트 메뉴 · 미리보기 · 폴더 공유 · 새 폴더 대화상자.
 *
 * 네 개 모두 "목록 위에 겹쳐 뜨고, 자기 상태 하나로 열림이 결정되고, 닫으면
 * 그 상태만 비운다" 는 같은 모양이다. 목록 렌더링과 섞여 있으면 페이지가
 * 무엇을 그리는지 읽기 어려워지므로 여기로 모았다.
 */
export function FileOverlays({
  ns, path, items, selected, canEdit, sharingEnabled,
  actions, menu, state, controls,
}: Props) {
  const { menuTarget, previewNode, shareTarget, newFolderOpen } = state;

  return (
    <>
      {menu.position && menuTarget && (
        <ContextMenu
          position={menu.position}
          onClose={menu.close}
          actions={buildActions({
            node: menuTarget,
            // 여러 개 골랐으면 고른 것 전체에, 하나면 오른쪽 클릭한 것에 적용한다
            selectedPaths: selected.length > 1 ? selected : [menuTarget.path],
            canEdit,
            currentPath: path,
            actions,
            onRename: () => { controls.setRenamingPath(menuTarget.path); },
            onDetails: () => { controls.setDrawerNode(menuTarget); },
            onPreview: () => { controls.setPreviewNode(menuTarget); },
            onShareFolder: () => { controls.setShareTarget(menuTarget); },
            canShareFolder: ns.type === 'personal' && ns.role === 'owner',
            sharingEnabled,
          })}
        />
      )}

      {previewNode && (
        <PreviewModal
          namespaceId={ns.id}
          node={previewNode}
          // 미리보기 안에서 좌우로 넘기려면 같은 폴더의 형제가 필요하다
          siblings={items}
          onNavigate={controls.setPreviewNode}
          onDownload={(node) => { void actions.download([node.path], node.name); }}
          onClose={() => { controls.setPreviewNode(null); }}
        />
      )}

      {shareTarget && (
        <ShareFolderDialog
          namespaceId={ns.id}
          node={shareTarget}
          onClose={() => { controls.setShareTarget(null); }}
        />
      )}

      {newFolderOpen && (
        <NewFolderDialog
          onClose={() => { controls.setNewFolderOpen(false); }}
          onCreate={(name) => {
            actions.newFolder.mutate(joinPath(path, name));
            controls.setNewFolderOpen(false);
          }}
        />
      )}
    </>
  );
}
